import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { GraphQLError } from 'graphql/error';
import i18n from '../i18n';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  FetchResult,
  from,
  HttpLink,
  NormalizedCacheObject,
  Observable,
} from '@apollo/client';
import { ContextSetter, setContext } from '@apollo/client/link/context';
import { ErrorHandler, onError } from '@apollo/client/link/error';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { CachePersistor, SessionStorageWrapper } from 'apollo3-cache-persist';
import { startSeedWriter } from './cacheSeed';
import { restoreApolloCache } from './restoreApolloCache';
import { FreshnessCache } from './freshnessCache';
import { FreshnessLink } from './freshnessLink';
import { applyFreshnessPolicy, CACHE_FRESHNESS_MS, CACHE_RETENTION_MS, RETENTION_SWEEP_MS } from './staleCachePolicy';
import { completeBoot } from '../utils/bootTrace';
import { MockApolloClient, createMockClient } from 'mock-apollo-client';
import { RetryLink } from '@apollo/client/link/retry';
import { shouldRetryOperation } from './retryPolicy';
import { createRefreshSingleFlight } from './refreshSession';
import { createEndDeadSession } from './endDeadSession';
import {
  isSessionEndedError,
  isRefreshUnavailableError,
  isSessionChangedError,
  SessionChangedError,
} from './refreshFailure';
import { onUnauthenticated, pinSession, type SessionPinContext } from './sessionPin';
import { leavePageWithoutWarning } from '../offline/useWarnOnUnloadWithPendingWork';
import { SentryLink } from 'apollo-link-sentry';
import { OfflineQueueLink } from '../offline/OfflineQueueLink';
import { OfflineCacheFallbackLink } from '../offline/OfflineCacheFallbackLink';
import { createOrderFlagsBarrierLink } from './orderFlagsBarrierLink';
import {
  isOnlineNow,
  isEffectivelyOnline,
  subscribeConnectivity,
} from '../offline/connectivity';
import { setBackendReachable } from '../offline/backendReachability';
import { startReachabilityHeartbeat } from '../offline/reachabilityHeartbeat';
import { apiEndpointManager } from '../services/apiEndpointManager';
import { serverDownBreaker } from '../offline/serverDownBreaker';

// Sentry
import * as Sentry from '@sentry/react';

// Mocks
import { addMockHandlersToClient } from './mocks';

// Service
import { refreshToken } from './services/auth';

// Utils
import {
  getSessionStorageUser,
  setSessionStorageUser,
} from '../utils/sessionStorage';

// Policies
import { policies } from './policies';

// Splash de arranque (evita el blanco puro mientras se restaura la caché)
import { SplashScreen } from '../components/display/SplashScreen';

// ───────────────────────────────────────────────────────────────────────────
// Puerta única del refresco (spec 2026-10-01 § 5.2).
// Sigue siendo single-flight (N operaciones que caducan a la vez → UN refresco)
// y ahora es además el ÚNICO sitio que puede cerrar la sesión: solo cuando el
// propio refresco se rechaza con un código de sesión muerta. Un 403 de una
// consulta normal es de permisos y lo trata su pantalla.
// `client` y `cacheApolloPersistor` se leen al LLAMAR, no al definir: para
// entonces ya existen.
// ───────────────────────────────────────────────────────────────────────────
const endDeadSession = createEndDeadSession({
  clearSession: () => setSessionStorageUser(null),
  // `FreshnessCache` conserva su interruptor tras `reset()`: no hay nada que
  // volver a aplicar (spec § 7, Fase 3b).
  clearApolloCache: () => (client ? client.clearStore().then(() => undefined) : Promise.resolve()),
  purgePersistedCache: () =>
    cacheApolloPersistor ? cacheApolloPersistor.purge() : Promise.resolve(),
  currentPath: () =>
    typeof window !== 'undefined' ? window.location.pathname : '/',
  // Sin el «¿salir de esta página?»: con trabajo en cola el aviso al cerrar
  // está armado, y esta recarga la hace la app, no el operario.
  navigate: (url) => {
    leavePageWithoutWarning();
    window.location.replace(url);
  },
  report: (reason) =>
    Sentry.captureMessage('[auth] sesión terminada por el servidor', {
      level: 'info',
      tags: { feature: 'auth', reason },
    }),
});

const refreshAccessTokenSingleFlight = createRefreshSingleFlight({
  // Sin refresh token se manda '' a propósito: el back responde
  // AUTH_REFRESH_EXPIRED y se sale al login, en vez de un error de validación
  // que dejaría la sesión zombi.
  refresh: (rt) => refreshToken(rt ?? ''),
  getSession: getSessionStorageUser,
  setSession: setSessionStorageUser,
  onSessionEnded: (reason) => {
    // Si una dependencia del cierre lanzara, que no quede un rechazo sin
    // manejar: se reporta y el operario sigue camino al login.
    endDeadSession(reason).catch((e) => Sentry.captureException(e));
  },
  onRefreshUnavailable: (error) => Sentry.captureException(error, { tags: { feature: 'auth', step: 'refresh-unavailable' } }),
});

if (import.meta.env.MODE === 'development') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

const useApolloMock = import.meta.env.VITE_USE_APOLLO_MOCK === 'true';

// La uri se resuelve EN CADA request (incl. reintentos de RetryLink): si el
// manager conmutó de endpoint entre intento e intento, el retry sale ya
// contra el respaldo.
const httpLink = new HttpLink({ uri: () => apiEndpointManager.currentUri() });

export let client: ApolloClient<NormalizedCacheObject>;

const offlineQueueLink = new OfflineQueueLink();
// Retiene los listados de «Solo marcadas» hasta que se guardan las marcas que
// ya estaban en vuelo (tope 3 s). El PRIMERO de la cadena; ver el link.
const orderFlagsBarrierLink = createOrderFlagsBarrierLink();

// Códigos estables del backend cuyo contrato es "aborto transitorio con
// rollback total — reintentar es seguro" (rois-back `business-error.helper.ts`
// + mapeo global de su `formatError`). El errorLink los reintenta UNA vez.
// La lista vive en `gql/retryableBackendCodes.ts`: la comparte la cola de
// subida de imágenes, que tampoco puede tratarlos como veneno permanente.
import {
  forwardAfter,
  RETRYABLE_BACKEND_CODES,
  TRANSIENT_RETRY_DELAY_MS,
} from './retryableBackendCodes';

const retryLink = new RetryLink({
  attempts: {
    // Extraído a `retryPolicy.ts` para poder probarlo: un aborto (plazo de fase
    // vencido) no se reintenta ni cuenta como endpoint caído.
    retryIf: (error, operation) =>
      shouldRetryOperation(error, {
        isOnlineNow,
        // Cortacircuitos (spec 2026-10-09): con el servidor comprobado caído no
        // se reintenta, y cada fallo de red cuenta para comprobarlo.
        isServerDown: () => serverDownBreaker.isOpen(),
        reportFailure: (e) => apiEndpointManager.reportFailure(e as any),
        reportServerFailure: (e) => serverDownBreaker.reportFailure(e),
        wasAborted: () =>
          Boolean(
            (operation.getContext() as { fetchOptions?: { signal?: AbortSignal } })
              .fetchOptions?.signal?.aborted,
          ),
      }),
  },
});
const sentryLink = new SentryLink({
  uri: import.meta.env.VITE_API_URL,
  setTransaction: false,
  attachBreadcrumbs: {
    includeQuery: true,
    // includeVariables:false → no adjuntar las variables de cada operación GraphQL
    // a los breadcrumbs (pueden contener passwords en login/reset, PII en formularios).
    includeVariables: false,
    includeError: true,
  },
});
const endpointHealthLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    apiEndpointManager.reportSuccess();
    serverDownBreaker.reportSuccess();
    // Una respuesta que vuelve prueba que el backend está: se sale del modo
    // sin red aquí mismo, sin esperar a otro sondeo (2026-09-11).
    setBackendReachable(true);
    return result;
  }),
);

// Frescura y retención separadas (spec 2026-10-01 § 7, Fase 3b): lo de más de
// 3 h se vuelve a pedir con red, pero no se borra al leerlo; si la red falla,
// el respaldo lo sirve.
export const cacheApolloSessionStorage = new FreshnessCache({
  typePolicies: policies,
  freshnessMs: CACHE_FRESHNESS_MS,
});

const offlineCacheFallbackLink = new OfflineCacheFallbackLink(
  cacheApolloSessionStorage
);
// Justo DEBAJO del respaldo: solo lo que llega del servidor pone edad.
const freshnessLink = new FreshnessLink(cacheApolloSessionStorage);

export let cacheApolloPersistor: CachePersistor<NormalizedCacheObject>;

/**
 * Retención (7 días sin respuesta de red) con red de seguridad: un fallo del
 * recorte se reporta y NUNCA impide arrancar ni entrar. Se pasa tras CADA
 * restauración —la del arranque y la de la semilla al entrar— y cada hora.
 */
export function pruneCacheRetention(step: string): void {
  try {
    cacheApolloSessionStorage.pruneRetention(CACHE_RETENTION_MS);
  } catch (e) {
    Sentry.captureException(e, { tags: { feature: 'boot', step } });
  }
}

export const AppApolloProvider = ({ children }: { children: ReactNode }) => {
  const [persistor, setPersistor] =
    useState<CachePersistor<NormalizedCacheObject>>();
  const setHeader = useCallback<ContextSetter>((_, context) => {
    const { headers } = context;
    const dataUser = getSessionStorageUser();

    return {
      // 🔴 Cada reintento de la operación (401, RetryLink, códigos
      // transitorios) vuelve a pasar por aquí: con otra sesión dentro, lanza
      // SessionChangedError en vez de salir con el token de otro (spec § 12.11).
      ...pinSession(context as SessionPinContext, dataUser),
      headers: {
        ...headers,
        authorization: dataUser?.accessToken
          ? `Bearer ${dataUser?.accessToken}`
          : '',
        // tenantId viene en el JWT payload, no es necesario enviarlo como header
      },
    };
  }, []);
  const authMiddleware = setContext(setHeader);

  const setError = useCallback<ErrorHandler>(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          // Abortos transitorios del backend con contrato "rollback total,
          // reintentar es seguro" (auditoría 2026-07-21, incidentes red
          // degradada 07-20/07-21): P2028→SAVE_TX_TIMEOUT, P2034→
          // SAVE_TX_CONFLICT, socket Mongo→DB_TRANSIENT_ERROR. El backend
          // los emite con código estable (por-sitio via `rethrowTxTimeout` o
          // globalmente en su `formatError`). Reenviar la MISMA operation
          // reutiliza variables (mismo clientOpId donde exista) → converge.
          // `forward(operation)` es retry-ONCE: Apollo no re-invoca este
          // handler para los errores del reintento. Se comprueba ANTES del
          // switch porque el `extensions.code` del driver varía; el contrato
          // es `extensions.originalError.code`.
          const backendCode = (
            err.extensions?.originalError as { code?: string } | undefined
          )?.code;
          if (backendCode && RETRYABLE_BACKEND_CODES.has(backendCode)) {
            // DBG 15-oct: con espera. Al instante caía en la misma ventana
            // del conflicto y volvía a chocar. Sigue siendo UN reintento.
            return forwardAfter(TRANSIENT_RETRY_DELAY_MS, () =>
              forward(operation)
            );
          }
          switch (err.extensions?.code) {
            case 'FORBIDDEN': {
              // Un 403 es de PERMISOS, nunca de sesión (RFC 9110 §15.5.4): lo
              // trata la pantalla. La sesión muerta la decide solo la puerta
              // única del refresco (spec 2026-10-01 § 5.2).
              return;
            }
            case 'UNAUTHENTICATED': {
              // ignore 401 error for a refresh request
              if (operation.operationName === 'RegenerateAccessToken') return;

              return new Observable<FetchResult>((observer) => {
                // used an annonymous function for using an async function
                (async () => {
                  try {
                    const user = getSessionStorageUser();
                    // Antes que nada: una 401 de A que llega cuando ya está B
                    // no refresca la sesión de B ni se repite con su token.
                    const action = onUnauthenticated(
                      operation.getContext() as SessionPinContext,
                      user,
                    );
                    if (action === 'session-changed') throw new SessionChangedError();
                    if (!user?.accessToken) {
                      throw new GraphQLError('Empty AccessToken');
                    }
                    // Single-flight: reusa el refresh en vuelo si ya hay uno
                    // (evita estampida de refrescos concurrentes y la rotación
                    // del refresh token en carrera). La sesión queda con el
                    // access token renovado tras await. Si otra consulta ya
                    // lo renovó, se repite sin refrescar otra vez.
                    if (action === 'refresh') await refreshAccessTokenSingleFlight();

                    // Retry the failed request
                    const subscriber = {
                      next: observer.next.bind(observer),
                      error: observer.error.bind(observer),
                      complete: observer.complete.bind(observer),
                    };

                    forward(operation).subscribe(subscriber);
                  } catch (err) {
                    // Una sesión terminada ya se reporta UNA vez en
                    // `endDeadSession`; reportarla por cada consulta inundaba
                    // Sentry (118 eventos en el incidente del 1-oct).
                    // Lo mismo para RefreshUnavailableError (red/plazo/5xx) —
                    // se reporta desde la puerta única — y SessionChangedError
                    // (logout + login concurrente) que no es un error real.
                    if (!isSessionEndedError(err) && !isRefreshUnavailableError(err) && !isSessionChangedError(err)) Sentry.captureException(err);
                    observer.error(err);
                  }
                })();
              });
            }
            case 'INTERNAL_SERVER_ERROR': {
              // Matches por TEXTO crudo — solo sirven contra un backend
              // VIEJO sin el mapeo transitorio del formatError (transición
              // de despliegue): con el backend nuevo estos mensajes ya no
              // viajan (los enmascara/mapea a código y los caza el check de
              // RETRYABLE_BACKEND_CODES de arriba). No añadir textos nuevos
              // aquí — todo transitorio nuevo debe salir con CÓDIGO estable.
              if (
                err?.message?.includes('Broken pipe') ||
                err?.message?.includes('Operation timed out') ||
                err?.message?.includes('write conflict or a deadlock')
              ) {
                return forward(operation);
              } else {
                return;
              }
            }
          }
        }
      }

      if (networkError)
        console.log('[apollo] error de red', networkError);
    },
    []
  );

  const errorLink = onError(setError);
  // TODO: fix persistCacheSync is necessary persistCache with loading

  useEffect(() => {
    async function init() {
      // (La reapertura sin cobertura se resuelve en `main.tsx`, ANTES de que
      // React monte nada. No puede hacerse aquí: `AuthProvider` está por ENCIMA
      // de este provider en el árbol y lee `sessionStorage` al montarse, así
      // que para cuando este `init()` restaurase la sesión, el router ya habría
      // decidido mandar a `/login`. Comprobado con el build de producción.)

      // Arranque en frío: si esta pestaña no tiene snapshot (pestaña nueva,
      // app reabierta) pero hay semilla guardada para el usuario en sesión, se
      // copia a `sessionStorage` ANTES de crear el persistor. Así la restaura
      // por su cauce normal y la forma del arranque no cambia.
      // Sin sesión legible no hace nada: preferimos arrancar en frío antes que
      // arriesgar servirle a alguien la caché de otro.
      //
      // 🔴 Con PLAZO, y recuperándose de un snapshot ilegible: hasta que esto
      // termina el provider solo enseña el splash, y las dos cosas podían
      // dejarlo ahí para siempre. Ver `restoreApolloCache`.
      cacheApolloPersistor = await restoreApolloCache(() => new CachePersistor({
        cache: cacheApolloSessionStorage,
        storage: new SessionStorageWrapper(window.sessionStorage),
        // Sin maxSize, apollo3-cache-persist aplica su default de 1 MB con un
        // comportamiento traicionero (Persistor.persist): si el snapshot
        // serializado supera el límite, hace purge() del guardado y PAUSA la
        // persistencia el resto de la sesión, sin error ni aviso. Con un par
        // de listados cargados se superaba → todo F5/pestaña nueva arrancaba
        // con caché vacía. 4 MB da holgura real (lo estable pesa ~100 KB tras
        // los recortes) y queda por debajo del umbral de corrupción de 5 MB
        // que vigila versionManager.checkCacheCorruption().
        //
        // ⚠️ Esta línea YA se perdió una vez: entró en `147550c5` (PR #81) y
        // la borró `383f6c08`, un commit de resolución de conflicto cuyo
        // mensaje solo hablaba de `orders.ts`. Estuvo semanas fuera, en
        // producción, sin que nada fallara de forma visible. Por eso hay un
        // tripwire: tests/e2e/specs/offline/cache-persistor-config.spec.ts.
        maxSize: 4 * 1024 * 1024,
      }));
      // Sin servidor, lo caducado se sirve sin intentar la red; con él, se
      // vuelve a pedir (spec 2026-10-01 § 7). Antes de crear el cliente, para
      // que el primer render ya lo sepa.
      applyFreshnessPolicy(cacheApolloSessionStorage, isEffectivelyOnline());
      // Retención: lo que lleva días sin respuesta de red se quita, para que el
      // snapshot no crezca sin fin. No asegura quedar bajo los 4 MB del
      // persistor: si en 7 días se juntan más, lo purga entero, igual que antes.
      //
      // 🔴 `pruneCacheRetention` lleva try/catch: sin recorte se pierde algo de
      // espacio; sin arranque se pierde la app entera. Si esto lanzase, `init()`
      // no llegaría a crear el cliente y el provider se quedaría en el splash en
      // CADA arranque, porque el snapshot y la semilla persisten (la misma clase
      // de cuelgue que documenta `restoreApolloCache`).
      pruneCacheRetention('apollo:prune');
      setInterval(() => pruneCacheRetention('apollo:prune-interval'), RETENTION_SWEEP_MS);
      // Mantener la semilla al día para el PRÓXIMO arranque en frío. Sin
      // identidad en sesión no escribe nada, así que arrancarlo aquí es seguro
      // aunque todavía estemos en la pantalla de login.
      startSeedWriter();
      setPersistor(cacheApolloPersistor);
      client = useApolloMock
        ? createMockClient()
        : new ApolloClient({
            link: from([
              orderFlagsBarrierLink,
              offlineCacheFallbackLink,
              freshnessLink,
              offlineQueueLink,
              retryLink,
              errorLink,
              authMiddleware,
              sentryLink,
              endpointHealthLink,
              httpLink,
            ]),
            cache: cacheApolloSessionStorage,
            connectToDevTools: true,
          });

      // Toggle network fetches based on connectivity
      // When offline, converts network-only/cache-and-network to cache-first
      // `isEffectivelyOnline` = enganchado a una red Y el backend contesta. Con
      // solo lo primero, una tablet en un hotspot sin datos se creía online y
      // cada pantalla se quedaba esperando a la red en vez de tirar de caché.
      client.prioritizeCacheValues = !isEffectivelyOnline();
      subscribeConnectivity((online) => {
        client.prioritizeCacheValues = !online;
        applyFreshnessPolicy(cacheApolloSessionStorage, online);
      });
      // Salida del modo caché por su cuenta: antes dependía, por casualidad, del
      // sondeo del cronómetro (spec 2026-09-28-cola-compound-y-latido).
      startReachabilityHeartbeat();

      if (useApolloMock) {
        addMockHandlersToClient(client as MockApolloClient);
      }

      // AQUÍ, y no al montar React, termina el arranque: hasta este punto el
      // operario solo ve el splash. Dar el arranque por bueno antes escondía
      // justo el cuelgue que viene después.
      completeBoot();
    }
    init().catch((error) => {
      // Antes solo `console.error`: el provider se quedaba en el splash sin que
      // nadie lo supiera. El arranque siguiente ya lo denunciará (no se llegó a
      // `completeBoot`), pero la causa tiene que estar en Sentry.
      console.error(error);
      Sentry.captureException(error, { tags: { feature: 'boot', step: 'apollo:init' } });
    });
  }, []);

  return persistor && client ? (
    <ApolloProvider client={client}>{children}</ApolloProvider>
  ) : (
    <SplashScreen />
  );
};
